import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CTEyeClientResponse, CompanyModel, RegisterClientRequest } from '../../../../data/entities';
import CTEyeService from '../../../../services/CTEyeService';
import CompanyService from '../../../../services/CompanyService';
import { showErrorMessage, showSuccessMessage } from '../../../../app/tools';
import { SlideOutModal, SpinnerDefault, Icon, IconDefinitions, InfoDetail, InfoSection, SectionHeader } from '../../../../components';
import sharedProperty from '../../../../app/sharedProperty';

interface ApproveClientModalProps {
  showState: sharedProperty<boolean>;
  client: CTEyeClientResponse | null;
  onSuccess: () => void;
}

const schema = yup.object({
  companyId: yup.number().typeError('A company must be selected').required('A company must be selected'),
  monthlyInspectionLimit: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  monthlyReportLimit: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
});

export function ApproveClientModal({ showState, client, onSuccess }: ApproveClientModalProps) {
  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const formik = useFormik({
    initialValues: {
      companyId: '' as number | '',
      monthlyInspectionLimit: '' as number | '',
      monthlyReportLimit: '' as number | '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!client) return;
      setLoading(true);
      try {
        const request: RegisterClientRequest = {
          companyId: values.companyId as number,
          monthlyInspectionLimit: values.monthlyInspectionLimit as number,
          monthlyReportLimit: values.monthlyReportLimit as number,
        };
        const success = await new CTEyeService().registerClient(client.id, request);
        if (success) {
          const desc = client.description || '';
          const machineName = desc.split(' - ')[0] || desc;
          const companyName = companies.find(c => c.id === Number(values.companyId))?.name ?? 'no company';
          showSuccessMessage(`Client "${machineName}" has been approved and assigned to ${companyName}`);
          showState.setter(false);
          onSuccess();
        } else {
          showErrorMessage('Failed to approve client. Please try again.');
        }
      } catch (error) {
        showErrorMessage('An error occurred while approving the client');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (showState.value) {
      loadCompanies();
      formik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showState.value]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const companies = await new CompanyService().getAllCompanies();
      setCompanies(companies);
    } catch (error) {
      console.error('Error loading companies:', error);
      showErrorMessage('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleCancel = () => {
    showState.setter(false);
  };

  if (!client) return null;

  const description = client.description || '';
  const [machineName, userName] = description.split(' - ');

  const clientDetails: InfoDetail[] = [
    {
      label: "Machine",
      value: machineName || '—',
      icon: <Icon iconName={IconDefinitions.desktop} />,
    },
    {
      label: "User",
      value: userName || '—',
      icon: <Icon iconName={IconDefinitions.user} />,
    },
    {
      label: "Version",
      value: <code>{client.runningVersion}</code>,
      icon: <Icon iconName={IconDefinitions.code} />,
    },
  ];

  return (
    <SlideOutModal
      showState={showState}
      title="Approve Client"
      footer={
        <div className="row m-0 w-100">
          <div className="col-6 ps-0">
            <Button
              onClick={handleCancel}
              variant="cancel"
              className="w-100"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
          <div className="col-6 pe-0">
            <Button
              onClick={() => formik.handleSubmit()}
              variant="primary"
              className="w-100"
              disabled={loading || loadingCompanies}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Approving...
                </>
              ) : (
                'Approve'
              )}
            </Button>
          </div>
        </div>
      }
    >
      {loadingCompanies ? (
        <div className="text-center py-4">
          <SpinnerDefault />
        </div>
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <InfoSection title="Client Information" details={clientDetails} />
          <div className="mb-3">
            <SectionHeader title="Approval Settings" />
            <div className="px-3 pt-3">
              <Form.Group className="mb-3">
                <Form.Label>Assign to Company</Form.Label>
                <Form.Select
                  name="companyId"
                  value={formik.values.companyId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  isInvalid={formik.touched.companyId && !!formik.errors.companyId}
                >
                  <option value="" disabled>-- Select a Company --</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.companyId}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Monthly Inspection Limit</Form.Label>
                <Form.Control
                  type="number"
                  name="monthlyInspectionLimit"
                  value={formik.values.monthlyInspectionLimit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onFocus={(e) => e.target.select()}
                  placeholder="e.g. 100"
                  disabled={loading}
                  isInvalid={formik.touched.monthlyInspectionLimit && !!formik.errors.monthlyInspectionLimit}
                />
                {formik.touched.monthlyInspectionLimit && formik.errors.monthlyInspectionLimit ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.monthlyInspectionLimit}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Text className="text-muted">
                    Maximum inspections allowed per month (0 = unlimited)
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Monthly Report Limit</Form.Label>
                <Form.Control
                  type="number"
                  name="monthlyReportLimit"
                  value={formik.values.monthlyReportLimit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onFocus={(e) => e.target.select()}
                  placeholder="e.g. 50"
                  disabled={loading}
                  isInvalid={formik.touched.monthlyReportLimit && !!formik.errors.monthlyReportLimit}
                />
                {formik.touched.monthlyReportLimit && formik.errors.monthlyReportLimit ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.monthlyReportLimit}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Text className="text-muted">
                    Maximum reports allowed per month (0 = unlimited)
                  </Form.Text>
                )}
              </Form.Group>

              {formik.values.companyId !== '' && (
                <div className="alert alert-info py-2 mb-0">
                  <small className="d-flex">
                    <span className="me-1 flex-shrink-0">
                      <Icon iconName={IconDefinitions.circleInfo} />
                    </span>
                    <span>
                      This client will be registered and assigned to{' '}
                      <strong>{companies.find(c => c.id === Number(formik.values.companyId))?.name}</strong>
                    </span>
                  </small>
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </SlideOutModal>
  );
}

export default ApproveClientModal;